import { useEffect, useMemo, useState } from "react";
import {
    FiRepeat,

    FiSave,
    FiX,
    FiXCircle,
} from "react-icons/fi";

const initialState = {
    tipoMovimiento: "GASTO",
    fechaMovimiento: "",
    fechaPago: "",
    descripcion: "",
    monto: "",
    idCuenta: "",
    idCategoria: "",
    idSubcategoria: "",
    idMoneda: "",
    idMetodoPago: "",
    tercero: "",
    idTipoComprobante: "",
    numeroComprobante: "",
    observacion: "",
    esRecurrente: false,
    esFijo: false,
    esDeducible: false,
    estado: "REGISTRADO",
};

export default function MovimientoForm({
                                           title = "Nuevo movimiento",
                                           initialData,
                                           onClose,
                                           onSave,
                                           cuentas = [],
                                           categorias = [],
                                           subcategorias = [],
                                           monedas = [],
                                           metodosPago = [],
                                           tiposComprobante = [],
                                       }) {
    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initialData) {
            setForm({
                ...initialState,
                ...initialData,
                tipoMovimiento:
                    initialData.tipoMovimiento ??
                    initialData.tipo_movimiento ??
                    "GASTO",
                fechaMovimiento:
                    initialData.fechaMovimiento ??
                    initialData.fecha_movimiento ??
                    "",
                fechaPago:
                    initialData.fechaPago ??
                    initialData.fecha_pago ??
                    "",
                idCuenta:
                    initialData.idCuenta ??
                    initialData.id_cuenta ??
                    "",
                idCategoria:
                    initialData.idCategoria ??
                    initialData.id_categoria ??
                    "",
                idSubcategoria:
                    initialData.idSubcategoria ??
                    initialData.id_subcategoria ??
                    "",
                idMoneda:
                    initialData.idMoneda ??
                    initialData.id_moneda ??
                    "",
                idMetodoPago:
                    initialData.idMetodoPago ??
                    initialData.id_metodo_pago ??
                    "",
                idTipoComprobante:
                    initialData.idTipoComprobante ??
                    initialData.id_tipo_comprobante ??
                    "",
                numeroComprobante:
                    initialData.numeroComprobante ??
                    initialData.numero_comprobante ??
                    "",
                esRecurrente:
                    Boolean(initialData.esRecurrente ?? initialData.es_recurrente),
                esFijo:
                    Boolean(initialData.esFijo ?? initialData.es_fijo),
                esDeducible:
                    Boolean(initialData.esDeducible ?? initialData.es_deducible),
            });
        } else {
            setForm(initialState);
        }
    }, [initialData]);

    const subcategoriasFiltradas = useMemo(() => {
        if (!form.idCategoria) return [];
        return subcategorias.filter(
            (item) =>
                String(item.id_categoria ?? item.idCategoria) ===
                String(form.idCategoria)
        );
    }, [form.idCategoria, subcategorias]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!form.tipoMovimiento) {
            nextErrors.tipoMovimiento = "Seleccione el tipo de movimiento";
        }

        if (!form.fechaMovimiento) {
            nextErrors.fechaMovimiento = "La fecha de movimiento es obligatoria";
        }

        if (!String(form.descripcion || "").trim()) {
            nextErrors.descripcion = "La descripción es obligatoria";
        }

        if (!form.monto || Number(form.monto) <= 0) {
            nextErrors.monto = "El monto debe ser mayor a 0";
        }

        if (!form.idCuenta) {
            nextErrors.idCuenta = "Seleccione una cuenta";
        }

        if (!form.idCategoria) {
            nextErrors.idCategoria = "Seleccione una categoría";
        }

        if (!form.idMoneda) {
            nextErrors.idMoneda = "Seleccione una moneda";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        await onSave({
            tipoMovimiento: form.tipoMovimiento,
            fechaMovimiento: form.fechaMovimiento,
            fechaPago: form.fechaPago || null,
            descripcion: form.descripcion,
            monto: Number(form.monto || 0),
            idCuenta: Number(form.idCuenta),
            idCategoria: Number(form.idCategoria),
            idSubcategoria: form.idSubcategoria ? Number(form.idSubcategoria) : null,
            idMoneda: Number(form.idMoneda),
            idMetodoPago: form.idMetodoPago ? Number(form.idMetodoPago) : null,
            tercero: form.tercero || null,
            idTipoComprobante: form.idTipoComprobante ? Number(form.idTipoComprobante) : null,
            numeroComprobante: form.numeroComprobante || null,
            observacion: form.observacion || null,
            esRecurrente: !!form.esRecurrente,
            esFijo: !!form.esFijo,
            esDeducible: !!form.esDeducible,
            estado: form.estado || "REGISTRADO",
        });
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-card large">
                <div className="modal-header">
                    <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FiRepeat size={20} />
                        {title}
                    </h3>

                    <button className="btn-secondary" type="button" onClick={onClose}>
                        <FiX size={16} />
                    </button>
                </div>

                <form className="grid-form" onSubmit={handleSubmit}>
                    <div>
                        <label>Tipo de movimiento</label>
                        <select
                            name="tipoMovimiento"
                            value={form.tipoMovimiento}
                            onChange={handleChange}
                        >
                            <option value="GASTO">Gasto</option>
                            <option value="INGRESO">Ingreso</option>
                        </select>
                        {errors.tipoMovimiento && (
                            <small className="error-text">{errors.tipoMovimiento}</small>
                        )}
                    </div>

                    <div>
                        <label>Fecha del movimiento</label>
                        <input
                            type="date"
                            name="fechaMovimiento"
                            value={form.fechaMovimiento}
                            onChange={handleChange}
                        />
                        {errors.fechaMovimiento && (
                            <small className="error-text">{errors.fechaMovimiento}</small>
                        )}
                    </div>

                    <div>
                        <label>Fecha de pago</label>
                        <input
                            type="date"
                            name="fechaPago"
                            value={form.fechaPago}
                            onChange={handleChange}
                        />
                    </div>

                    <div>
                        <label>Descripción</label>
                        <input
                            type="text"
                            name="descripcion"
                            placeholder="Ej. Compra de útiles, pago de taxi, cobro de servicio"
                            value={form.descripcion}
                            onChange={handleChange}
                        />
                        {errors.descripcion && (
                            <small className="error-text">{errors.descripcion}</small>
                        )}
                    </div>

                    <div>
                        <label>Monto</label>
                        <input
                            type="number"
                            step="0.01"
                            name="monto"
                            placeholder="0.00"
                            value={form.monto}
                            onChange={handleChange}
                        />
                        {errors.monto && (
                            <small className="error-text">{errors.monto}</small>
                        )}
                    </div>

                    <div>
                        <label>
                            {form.tipoMovimiento === "GASTO"
                                ? "Proveedor / tercero"
                                : "Cliente / fuente"}
                        </label>
                        <input
                            type="text"
                            name="tercero"
                            placeholder={
                                form.tipoMovimiento === "GASTO"
                                    ? "Ej. Proveedor, tienda, persona"
                                    : "Ej. Cliente, empresa, persona"
                            }
                            value={form.tercero}
                            onChange={handleChange}
                        />
                    </div>

                    <div>
                        <label>Cuenta</label>
                        <select
                            name="idCuenta"
                            value={form.idCuenta}
                            onChange={handleChange}
                        >
                            <option value="">Seleccione cuenta</option>
                            {cuentas.map((item) => {
                                const id = item.idCuenta ?? item.id_cuenta;
                                const nombre = item.nombre;
                                const moneda = item.monedaCodigo ?? item.moneda_codigo ?? "";
                                return (
                                    <option key={id} value={id}>
                                        {nombre} {moneda ? `- ${moneda}` : ""}
                                    </option>
                                );
                            })}
                        </select>
                        {errors.idCuenta && (
                            <small className="error-text">{errors.idCuenta}</small>
                        )}
                    </div>

                    <div>
                        <label>Categoría</label>
                        <select
                            name="idCategoria"
                            value={form.idCategoria}
                            onChange={(e) => {
                                handleChange(e);
                                setForm((prev) => ({
                                    ...prev,
                                    idSubcategoria: "",
                                }));
                            }}
                        >
                            <option value="">Seleccione categoría</option>
                            {categorias.map((item) => {
                                const id = item.idCategoria ?? item.id_categoria;
                                return (
                                    <option key={id} value={id}>
                                        {item.nombre}
                                    </option>
                                );
                            })}
                        </select>
                        {errors.idCategoria && (
                            <small className="error-text">{errors.idCategoria}</small>
                        )}
                    </div>

                    <div>
                        <label>Subcategoría</label>
                        <select
                            name="idSubcategoria"
                            value={form.idSubcategoria}
                            onChange={handleChange}
                        >
                            <option value="">Seleccione subcategoría</option>
                            {subcategoriasFiltradas.map((item) => {
                                const id = item.idSubcategoria ?? item.id_subcategoria;
                                return (
                                    <option key={id} value={id}>
                                        {item.nombre}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <label>Moneda</label>
                        <select
                            name="idMoneda"
                            value={form.idMoneda}
                            onChange={handleChange}
                        >
                            <option value="">Seleccione moneda</option>
                            {monedas.map((item) => {
                                const id = item.idMoneda ?? item.id_moneda;
                                return (
                                    <option key={id} value={id}>
                                        {item.codigo} - {item.nombre}
                                    </option>
                                );
                            })}
                        </select>
                        {errors.idMoneda && (
                            <small className="error-text">{errors.idMoneda}</small>
                        )}
                    </div>

                    <div>
                        <label>Método de pago</label>
                        <select
                            name="idMetodoPago"
                            value={form.idMetodoPago}
                            onChange={handleChange}
                        >
                            <option value="">Seleccione método de pago</option>
                            {metodosPago.map((item) => {
                                const id = item.idMetodoPago ?? item.id_metodo_pago;
                                return (
                                    <option key={id} value={id}>
                                        {item.nombre}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <label>Tipo de comprobante</label>
                        <select
                            name="idTipoComprobante"
                            value={form.idTipoComprobante}
                            onChange={handleChange}
                        >
                            <option value="">Seleccione tipo comprobante</option>
                            {tiposComprobante.map((item) => {
                                const id =
                                    item.idTipoComprobante ?? item.id_tipo_comprobante;
                                return (
                                    <option key={id} value={id}>
                                        {item.nombre}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <label>Número de comprobante</label>
                        <input
                            type="text"
                            name="numeroComprobante"
                            placeholder="Ej. F001-12345"
                            value={form.numeroComprobante}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="full-width">
                        <label>Observación</label>
                        <textarea
                            name="observacion"
                            placeholder="Agrega un detalle adicional si lo necesitas"
                            value={form.observacion}
                            onChange={handleChange}
                        />
                    </div>

                    <label className="check-row">
                        <input
                            type="checkbox"
                            name="esRecurrente"
                            checked={form.esRecurrente}
                            onChange={handleChange}
                        />
                        Recurrente
                    </label>

                    <label className="check-row">
                        <input
                            type="checkbox"
                            name="esFijo"
                            checked={form.esFijo}
                            onChange={handleChange}
                        />
                        Fijo
                    </label>

                    <label className="check-row">
                        <input
                            type="checkbox"
                            name="esDeducible"
                            checked={form.esDeducible}
                            onChange={handleChange}
                        />
                        Deducible
                    </label>

                    <div className="form-actions full-width">
                        <button className="btn-secondary" type="button" onClick={onClose}>
                            <FiXCircle size={16} />
                            Cancelar
                        </button>

                        <button className="btn-primary" type="submit">
                            <FiSave size={16} />
                            Guardar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}